<template>
    <div id="summaryEventRulePanel" class="form-wrapper-summary">
        <PanelInput v-model="localForm.ruleName" :label="$t('ruleName')" :placeholder="$t('enterRuleName')"
            :max-length="100" :tooltip="$t('tooltips.summaryEvents.ruleName')" :form-type="'summaryEvent'"
            style="padding-left: 1rem; padding-right: 1rem;" />

        <div class="back-container">
            <div class="section-container">
                <PanelDropdown v-model="localForm.eventType" :label="$t('eventtype')" :options="eventTypeOpts"
                    :placeholder="$t('selectEventType')" :tooltip="$t('tooltips.summaryEvents.eventType')" option-label="label" :inputType="'summaryEventType'" :filter="true" />
                <div style="margin-top: 20px;">
                    <div class="section-header">
                        <label class="form-label">{{ $t('groupBy') }}</label>
                        <Tooltip :text="$t('tooltips.summaryEvents.groupBy.groupBy')">
                            <i class="ag-icon ag-icon_table_info small info-icon"></i>
                        </Tooltip>
                    </div>
                    <span class="section-subtitle">{{ $t('groupBySubtitle') }}</span>
                </div>

                <div class="radius-box">
                    <div v-for="(groupField, index) in localForm.groupByFields" :key="'group-' + index"
                        class="field-row" style="width: 100%;">

                        <div class="dual-dropdown-row">
                            <div class="dropdown-wrapper">
                                <Dropdown v-model="groupField.elementName" :options="groupingFieldOptions"
                                    optionLabel="value" optionValue="value" :placeholder="$t('select')" appendTo="self" filter
                                    :class="{ 'p-invalid': groupFieldErrors[index] }"
                                    @change="onGroupFieldElementNameChange(index)" />
                                <small v-if="groupFieldErrors[index]" class="p-error">
                                    <i class="pi pi-exclamation-circle"></i>
                                    {{ $t('fieldCannotBeEmpty') }}
                                </small>
                            </div>
                            <div class="dropdown-wrapper">
                                <Dropdown v-model="groupField.level" :options="getGroupLevelOptions(groupField.elementName)" optionLabel="text"
                                    optionValue="value" :placeholder="$t('selectLevel')" appendTo="self" 
                                    :disabled="!groupField.elementName || (!groupField.elementName.includes('ancestors.ancestor') && !groupField.elementName.includes('publishers.publisher'))"
                                    :key="`level-${index}-${groupField.elementName}`">
                                    <template #value="slotProps">
                                        <span v-if="groupField.level !== null && groupField.level !== undefined">
                                            {{ getGroupLevelOptions(groupField.elementName).find(opt => opt.value === groupField.level)?.text || slotProps.placeholder }}
                                        </span>
                                        <span v-else>{{ slotProps.placeholder }}</span>
                                    </template>
                                </Dropdown>
                            </div>
                            <button type="button" class="remove-btn"
                                @click="removeGroupField(index)" :title="$t('remove')">
                                <i class="ag-icon ag-icon_icon_delete"></i>
                            </button>
                        </div>
                    </div>
                    </div>

                <button type="button" class="add-field-btn" @click="addGroupField">
                    <i class="pi pi-plus"></i>
                    <span>{{ $t('addGroupingField').toUpperCase() }}</span>
                    <Tooltip :text="$t('tooltips.summaryEvents.groupBy.addGroupingField')">
                        <i class="ag-icon ag-icon_table_info small info-icon"></i>
                    </Tooltip>
                </button>
            </div>

            <!-- Conditions Section -->
            <div class="section-container" style="padding-bottom: 1rem;">
                <div class="section-header">
                    <label class="form-label">{{ $t('conditions') }}</label>
                    <Tooltip :text="$t('tooltips.summaryEvents.conditions.condition')">
                        <i class="ag-icon ag-icon_table_info small info-icon"></i>
                    </Tooltip>
                </div>
                <span class="section-subtitle">{{ $t('conditionsSubtitle') }}</span>
                <div class="conditions-wrapper">
                    <div class="and-pill">AND</div>
                    <div class="conditions-box">
                        <div class="radius-box">
                        <div class="conditions-labels">
                        <label class="dropdown-label">
                            {{ $t('property') }}
                            <Tooltip :text="$t('tooltips.summaryEvents.conditions.property')">
                                <i class="ag-icon ag-icon_table_info small info-icon"></i>
                            </Tooltip>
                        </label>
                        <label class="dropdown-label">
                            {{ $t('operator') }}
                            <Tooltip :text="$t('tooltips.summaryEvents.conditions.operator')">
                                <i class="ag-icon ag-icon_table_info small info-icon"></i>
                            </Tooltip>
                        </label>
                        <label class="dropdown-label">
                            {{ $t('value') }}
                            <Tooltip :text="$t('tooltips.summaryEvents.conditions.value')">
                                <i class="ag-icon ag-icon_table_info small info-icon"></i>
                            </Tooltip>
                        </label>
                        <label class="dropdown-label">
                            {{ $t('level') }}
                            <Tooltip :text="$t('tooltips.summaryEvents.conditions.level')">
                                <i class="ag-icon ag-icon_table_info small info-icon"></i>
                            </Tooltip>
                        </label>
                        <span style="width: 40px;"></span>
                    </div>

                    <div v-for="(condition, index) in localForm.conditions" :key="'condition-' + index"
                        class="field-row">
                        <div class="quad-dropdown-row">
                            <div class="dropdown-item-small dropdown-wrapper">
                                <Dropdown v-model="condition.elementName" :options="propertyOptions" optionLabel="value"
                                    optionValue="value" :placeholder="$t('selectProperty')" appendTo="self" filter
                                    :class="{ 'p-invalid': conditionErrors[index] }"
                                    @change="onConditionPropertyChange(index)">
                                    <template #value="slotProps">
                                        <span 
                                            :ref="el => handleConditionElementRef(el, index, condition.elementName)"
                                            class="dropdown-value-text"
                                            @mouseenter="(e) => handleTooltipCheck(e, index, condition.elementName)">
                                            {{ condition.elementName || slotProps.placeholder }}
                                        </span>
                                    </template>
                                </Dropdown>
                                <small v-if="conditionErrors[index]" class="p-error">
                                    <i class="pi pi-exclamation-circle"></i>
                                    {{ $t('fieldCannotBeEmpty') }}
                                </small>
                            </div>
                            <div class="dropdown-item-small dropdown-wrapper">
                                <Dropdown v-model="condition.condition" :options="operatorOptions" optionLabel="text"
                                    optionValue="value" :placeholder="$t('selectOperator')" appendTo="self" />
                            </div>
                            <div class="dropdown-wrapper">
                                <InputText v-model="condition.value" :placeholder="$t('enterValue')" :maxlength="260" style="width: 100%;" />
                            </div>
                            <div class="dropdown-item-small dropdown-wrapper">
                                <Dropdown v-model="condition.level" :options="getConditionLevelOptions(condition.elementName)" optionLabel="text"
                                    optionValue="value" :placeholder="$t('selectLevel')" appendTo="self" 
                                    :disabled="!condition.elementName || (!condition.elementName.includes('ancestors.ancestor') && !condition.elementName.includes('publishers.publisher'))"
                                    :key="`condition-level-${index}-${condition.elementName}`">
                                    <template #value="slotProps">
                                        <span v-if="condition.level !== null && condition.level !== undefined">
                                            {{ getConditionLevelOptions(condition.elementName).find(opt => opt.value === condition.level)?.text || slotProps.placeholder }}
                                        </span>
                                        <span v-else>{{ slotProps.placeholder }}</span>
                                    </template>
                                </Dropdown>
                            </div>
                            <button type="button" class="remove-btn"
                                @click="removeCondition(index)" :title="$t('remove')">
                                <i class="ag-icon ag-icon_icon_delete"></i>
                            </button>
                        </div>
                    </div>
                    </div>
                    </div>
                    
                </div>

                <button type="button" class="add-field-btn" @click="addCondition">
                    <i class="pi pi-plus"></i>
                    <span>{{ $t('addCondition').toUpperCase() }}</span>
                    <Tooltip :text="$t('tooltips.summaryEvents.conditions.addCondition')">
                        <i class="ag-icon ag-icon_table_info small info-icon"></i>
                    </Tooltip>
                </button>
            </div>
        </div>

        <ExpertNotes v-model="localForm.notes" :max-length="1000" :label="$t('notes')"
            :tooltip="$t('tooltips.summaryEvents.notes')" style="padding-left: 1rem; padding-right: 1rem; margin-top: 0; border-top: none !important;" />
    </div>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, onMounted, watch, defineExpose, nextTick } from 'vue'
import PanelInput from './components/panelInput.vue'
import PanelDropdown from './components/panelDropdown.vue'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import ExpertNotes from './components/expertNotes.vue'
import Tooltip from './components/tooltip.vue'
import { useStore } from "@/store"
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const store = useStore()

const groupFieldErrors = ref({})
const conditionErrors = ref({})
const conditionElementRefs = ref({})
const conditionElementOverflows = ref({})

const props = defineProps({
    form: {
        type: Object,
        required: true,
        default: () => ({
            ruleName: '',
            eventType: null,
            groupByFields: [{ elementName: null, level: null }],
            conditions: [{ elementName: null, condition: null, value: '', level: null }],
            notes: ''
        })
    },
    formType: {
        type: String,
        default: 'summaryEventRule'
    }
})

const emit = defineEmits(['update:form', 'validation'])

const validateForm = () => {
    groupFieldErrors.value = {}
    conditionErrors.value = {}
    let isValid = true

    const hasFilledGroupFields = localForm.value.groupByFields.some(field => 
        field.elementName || field.level
    )

    if (hasFilledGroupFields) {
        localForm.value.groupByFields.forEach((field, index) => {
            if (!field.elementName || field.elementName === null) {
                groupFieldErrors.value[index] = true
                isValid = false
            }
        })
    }

    const hasFilledConditions = localForm.value.conditions.some(condition => 
        condition.elementName || condition.condition || condition.value || condition.level
    )

    if (hasFilledConditions) {
        localForm.value.conditions.forEach((condition, index) => {
            if (!condition.elementName || condition.elementName === null) {
                conditionErrors.value[index] = true
                isValid = false
            }
        })
    }

    return isValid
}

const getCleanedFormData = () => {
    const cleanedData = { ...localForm.value }
    
    cleanedData.groupByFields = localForm.value.groupByFields.filter(field => 
        field.elementName || field.level
    )
    
    if (cleanedData.groupByFields.length === 0) {
        cleanedData.groupByFields = []
    }

    cleanedData.conditions = localForm.value.conditions.filter(condition => 
        condition.elementName || condition.condition || condition.value || condition.level
    )
    
    if (cleanedData.conditions.length === 0) {
        cleanedData.conditions = []
    }
    
    return cleanedData
}

defineExpose({
    validateForm,
    getCleanedFormData
})

const localForm = computed({
    get() {
        return props.form
    },
    set(newVal) {
        emit('update:form', newVal)
    }
})

const summaryEventEntityData = computed(() => store.state.general.summaryEventEntityData)
const eventTypeOptions = computed(() => summaryEventEntityData.value?.elementCategories || [])
const eventTypeOpts = computed(() => eventTypeOptions.value.map((opt) => ({ label: opt.text, value: opt.value })))
const allElementNames = computed(() => summaryEventEntityData.value?.elementNames || [])

const filteredElementNames = computed(() => {
    const eventType = localForm.value?.eventType
    if (eventType === '%EventType%') return allElementNames.value
    if (eventType === 'MemoryGuardEvent') {
        return allElementNames.value.filter(
            (p) => typeof p?.text === 'string' && p.text.startsWith(eventType)
        )
    }
    return allElementNames.value.filter(
        (p) => typeof p?.text === 'string'
            && (p.text.startsWith(eventType) || p.text.startsWith('%EventType%'))
    )
})
const groupingFieldOptions = computed(() => filteredElementNames.value)
const propertyOptions = computed(() => filteredElementNames.value)
const operatorOptions = computed(() => summaryEventEntityData.value?.conditions || [])
const levelOptions = computed(() => summaryEventEntityData.value?.positions || [])
const publisherLevelOptions = computed(() => summaryEventEntityData.value?.publisherPositions || [])

const getGroupLevelOptions = (elementName) => {
    if (elementName && elementName.includes('publishers.publisher')) {
        return publisherLevelOptions.value
    }
    return levelOptions.value
}

const getConditionLevelOptions = (elementName) => {
    if (elementName && elementName.includes('publishers.publisher')) {
        return publisherLevelOptions.value
    }
    return levelOptions.value
}

function ensureMinimumRows() {
    if (!localForm.value.groupByFields) {
        localForm.value.groupByFields = [{ elementName: null, level: null }]
    } else if (localForm.value.groupByFields.length === 0) {
        localForm.value.groupByFields.push({ elementName: null, level: null })
    }
    
    if (!localForm.value.conditions) {
        localForm.value.conditions = [{ elementName: null, condition: null, value: '', level: null }]
    } else if (localForm.value.conditions.length === 0) {
        localForm.value.conditions.push({ elementName: null, condition: null, value: '', level: null })
    }
}

function addGroupField() {
    if (!localForm.value.groupByFields) {
        localForm.value.groupByFields = []
    }
    localForm.value.groupByFields.push({ elementName: null, level: null })
}

function removeGroupField(index) {
    if (localForm.value.groupByFields.length > 1) {
        localForm.value.groupByFields.splice(index, 1)
    } else {
        localForm.value.groupByFields[0] = { elementName: null, level: null }
    }
}

function addCondition() {
    if (!localForm.value.conditions) {
        localForm.value.conditions = []
    }
    localForm.value.conditions.push({ elementName: null, condition: null, value: '', level: null })
}

function removeCondition(index) {
    if (localForm.value.conditions.length > 1) {
        localForm.value.conditions.splice(index, 1)
    } else {
        localForm.value.conditions[0] = { elementName: null, condition: null, value: '', level: null }
    }
}

watch(() => localForm.value?.eventType, () => {
    const validValues = new Set(
        filteredElementNames.value
            .map((p) => p?.value)
            .filter((v) => v !== undefined && v !== null)
    )

    if (Array.isArray(localForm.value?.groupByFields)) {
        localForm.value.groupByFields.forEach((g) => {
            if (g.elementName && !validValues.has(g.elementName)) {
                g.elementName = null
                g.level = null
            }
        })
    }

    if (Array.isArray(localForm.value?.conditions)) {
        localForm.value.conditions.forEach((c) => {
            if (c.elementName && !validValues.has(c.elementName)) {
                c.elementName = null
                c.level = null
            }
        })
    }
})
function onGroupFieldElementNameChange(index) {
    setTimeout(() => {
        const field = localForm.value.groupByFields[index]
        if (!field || !field.elementName) return
        
        const isAncestor = field.elementName.includes('ancestors.ancestor')
        const isPublisher = field.elementName.includes('publishers.publisher')
        const shouldEnableLevel = isAncestor || isPublisher
        
        if (shouldEnableLevel) {
            const levelOpts = getGroupLevelOptions(field.elementName)
            let defaultOption = null
            
            if (isPublisher) {
                // For publisher, select "All Certificates" (value: "0")
                defaultOption = levelOpts.find(opt => opt.text === 'All Certificates')
            } else if (isAncestor) {
                // For ancestor, select "All" (value: "")
                defaultOption = levelOpts.find(opt => opt.text === 'All')
            }
            
            if (defaultOption) {
                localForm.value.groupByFields[index].level = defaultOption.value
            }
        } else {
            localForm.value.groupByFields[index].level = null
        }
    }, 100)
}

function handleConditionElementRef(el, index, elementName) {
    if (el) {
        conditionElementRefs.value[index] = el
    }
}

function handleTooltipCheck(event, index, elementName) {
    if (!elementName) return
    
    const el = event.target
    if (el && el.scrollWidth > el.clientWidth) {
        el.setAttribute('title', elementName)
    } else {
        el.removeAttribute('title')
    }
}

function onConditionPropertyChange(index) {
    setTimeout(() => {
        const condition = localForm.value.conditions[index]
        if (!condition || !condition.elementName) return
        
        const equalToOption = operatorOptions.value.find(opt => opt.text === 'Equal To')
        
        if (equalToOption) {
            localForm.value.conditions[index].condition = equalToOption.value
        }
        
        const isAncestor = condition.elementName.includes('ancestors.ancestor')
        const isPublisher = condition.elementName.includes('publishers.publisher')
        
        if (isAncestor || isPublisher) {
            const levelOpts = getConditionLevelOptions(condition.elementName)
            let defaultOption = null
            
            if (isPublisher) {
                defaultOption = levelOpts.find(opt => opt.text === 'All Certificates')
            } else if (isAncestor) {
                defaultOption = levelOpts.find(opt => opt.text === 'All')
            }
            
            if (defaultOption) {
                localForm.value.conditions[index].level = defaultOption.value
            }
        } else {
            localForm.value.conditions[index].level = null
        }
    }, 100)
}

onMounted(() => {
    ensureMinimumRows()
})

watch(() => localForm.value.groupByFields, (newFields) => {
    newFields.forEach((field, index) => {
        if (field.elementName && groupFieldErrors.value[index]) {
            delete groupFieldErrors.value[index]
        }
    })
}, { deep: true })

watch(() => localForm.value.conditions, (newConditions) => {
    newConditions.forEach((condition, index) => {
        if (condition.elementName && conditionErrors.value[index]) {
            delete conditionErrors.value[index]
        }
    })
}, { deep: true })
</script>

<style scoped>
.form-wrapper-summary {
    font-size: 0.8rem !important;
}

.back-container {
    box-shadow: inset 0 3px 6px rgba(0, 0, 0, 0.1);
    margin-top: 8px;
    background-color: #f5f5f5;
}

.section-container {
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: 1rem;
    display: flex;
    flex-direction: column;
}

.section-header {
    display: flex;
    align-items: center;
    margin-bottom: 0.6rem;
}

.form-label {
    color: #164B6C;
    font-weight: bold;
    margin: 0;
    margin-right: 8px;
}

.info-icon {
    margin-left: 3px;
}

.field-row {
    margin-bottom: 0.75rem;
}

.dropdown-labels {
    display: grid;
    grid-template-columns: minmax(200px, 1fr) minmax(200px, 1fr) 40px;
    gap: 1rem;
    margin-bottom: 0.5rem;
}

.conditions-labels {
    display: grid;
    grid-template-columns: minmax(150px, 2fr) minmax(150px, 1fr) minmax(100px, 0.6fr) minmax(150px, 1fr) 40px;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
}

.dropdown-label {
    color: #164B6C;
    font-weight: 600;
    font-size: 0.8rem;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.dual-dropdown-row {
    display: grid;
    grid-template-columns: minmax(200px, 1fr) minmax(150px, 0.3fr) 40px;
    gap: 0.5rem;
    align-items:start;
}

.quad-dropdown-row {
    display: grid;
    grid-template-columns: minmax(150px, 2fr) minmax(150px, 1fr) minmax(100px, 0.6fr) minmax(150px, 1fr) 40px;
    gap: 0.75rem;
    align-items: start;
}

.dropdown-item {
    min-width: 200px;
}

.dropdown-item-small {
    min-width: 150px;
}

.remove-btn {
    background: transparent;
    border: none;
    
    border-radius: 4px;
    padding: 6px 10px;
    cursor: pointer;
    transition: all 0.2s;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
}



.add-field-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: transparent;
    border: none;
    color: #0055A6;
    padding: 8px 16px;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.8rem;
    transition: all 0.2s;
    margin-top: 0.5rem;
    align-self: flex-end;
}

.add-field-btn:hover {
    color: #003D7A;
}

.add-field-btn .pi-plus{
    font-size: 0.8rem;
}

.add-field-btn span {
    text-decoration: underline;
}
.conditions-wrapper {
    position: relative;
}

.and-pill {
    position: absolute;
    left: -10px;
    top: 50%;
    transform: translateY(-50%);
    writing-mode: sideways-lr;
    text-orientation: mixed;
    background-color: #0B5CAD;
    color: #fff;
    border-radius: 10px;
    padding: 6px 2px;
    font-weight: 700;
    font-size: 0.6rem;
    letter-spacing: 0.5px;
    z-index: 2;
    box-shadow: 0 2px 4px rgba(11, 92, 173, 0.2);
}

.radius-box {
    margin-top: 1rem;
    background-color: #fff;
    padding: 1rem;
    border-radius: 14px;
}

.conditions-box {
    border-left: 2px solid #0B5CAD; 
    border-radius: 15px;
    overflow: visible;
    background-color: #fff;
}

:deep(.p-dropdown) {
    height: 30px;
    width: 100%;
    align-items: center;
}

:deep(.p-dropdown .p-inputtext) {
    font-size: 0.8rem !important;
    padding: 0.25rem 0 0.25rem 0.25rem;
}

:deep(.p-dropdown-trigger) {
    justify-content: end;
    margin-right: 0.3rem;
    width: min-content;
}

:deep(.p-inputtext) {
    height: 30px;
    font-size: 0.8rem !important;
    padding: 0.25rem 0.5rem;
}

:deep(.p-dropdown-panel) {
    width: auto !important;
    min-width: 100% !important;
    max-width: 750px !important;
}

.dropdown-value-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
}

.section-subtitle {
    font-weight: 500;
    color: #000;
}

.dropdown-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.p-error {
    color: #ef4444;
    margin-top: 0.25rem;
    font-size: 0.75rem;
    line-height: 1.2rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.p-error .pi-exclamation-circle {
    font-size: 0.85rem;
}

:deep(.p-dropdown.p-invalid) {
    border-color: #ef4444;
}

/* Large Monitors & 4K Displays */
@media (min-width: 1920px) {
  .conditions-labels {
    grid-template-columns: minmax(150px, 3.8fr) minmax(120px, 1fr) minmax(80px, 0.5fr) minmax(110px, 0.9fr) 40px;
  }
  
  .quad-dropdown-row {
    grid-template-columns: minmax(150px, 3.8fr) minmax(120px, 1fr) minmax(80px, 0.5fr) minmax(110px, 0.9fr) 40px;
  }
}

/* Standard Desktop */
@media (min-width: 1440px) and (max-width: 1919px) {
  .conditions-labels {
    grid-template-columns: minmax(150px, 3fr) minmax(130px, 1fr) minmax(90px, 0.5fr) minmax(120px, 1fr) 40px;
  }
  
  .quad-dropdown-row {
    grid-template-columns: minmax(150px, 3fr) minmax(130px, 1fr) minmax(90px, 0.5fr) minmax(120px, 1fr) 40px;
  }
}

/* Laptop & Small Desktop */
@media (min-width: 1024px) and (max-width: 1439px) {
  .conditions-labels {
    grid-template-columns: minmax(150px, 2fr) minmax(150px, 1fr) minmax(100px, 0.6fr) minmax(150px, 1fr) 40px;
    gap: 0.5rem;
  }
  
  .quad-dropdown-row {
    grid-template-columns: minmax(150px, 2fr) minmax(150px, 1fr) minmax(100px, 0.6fr) minmax(150px, 1fr) 40px;
    gap: 0.5rem;
  }
  
  .dual-dropdown-row {
    grid-template-columns: minmax(200px, 1fr) minmax(150px, 0.3fr) 40px;
  }
}

/* Tablet & Small Laptop */
@media (min-width: 768px) and (max-width: 1023px) {
  .conditions-labels {
    grid-template-columns: minmax(120px, 1.5fr) minmax(100px, 1fr) minmax(80px, 0.6fr) minmax(100px, 1fr) 40px;
    gap: 0.5rem;
  }
  
  .quad-dropdown-row {
    grid-template-columns: minmax(120px, 1.5fr) minmax(100px, 1fr) minmax(80px, 0.6fr) minmax(100px, 1fr) 40px;
    gap: 0.5rem;
  }
  
  .dual-dropdown-row {
    grid-template-columns: minmax(150px, 1fr) minmax(120px, 0.5fr) 40px;
    gap: 0.5rem;
  }
  
  .dropdown-labels {
    grid-template-columns: minmax(150px, 1fr) minmax(120px, 0.8fr) 40px;
  }
}

</style>
